// The one read, write and removal of the browser stores a per-browser
// convenience makes.
//
// A private window, a full store and a thumbnail capture all throw on the
// call, and a page that cannot read its store simply has no memory. Every
// caller used to wrap the calls in its own `try`; these answer the same way
// for all of them. A read gives `null` where the store has no value or
// refuses, and the caller applies its own zero. A write or a removal that
// the store refuses is dropped without a word. The `local` area lasts on
// the device and the `session` area lasts as long as the tab. The module
// imports nothing.

/** The two browser stores: `local` (`localStorage`) and `session`
 *  (`sessionStorage`). */
export type StoreArea = 'local' | 'session';

/** The store of one area, or null where the page has none or refuses it.
 *  A caller that hands a store on (a report outbox) takes it here. */
export function storeOf(area: StoreArea = 'local'): Storage | null {
  try {
    return (area === 'local' ? globalThis.localStorage : globalThis.sessionStorage) ?? null;
  } catch {
    return null;
  }
}

/** The stored string, or `null` where the store has none or refuses. */
export function readStore(key: string, area: StoreArea = 'local'): string | null {
  try {
    return storeOf(area)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

/** Writes one value; a store that refuses just forgets. */
export function writeStore(key: string, value: string, area: StoreArea = 'local'): void {
  try {
    storeOf(area)?.setItem(key, value);
  } catch { /* private mode, a full store: the value lasts the session */ }
}

/** Removes one value; a store that refuses keeps what it holds. */
export function removeStore(key: string, area: StoreArea = 'local'): void {
  try {
    storeOf(area)?.removeItem(key);
  } catch { /* a blocked store: nothing to take away */ }
}

/**
 * A reader of one stored value that parses it again only when its text
 * changed: a caller that asks once for each line of a card pays one read
 * of the store each time and one parse.
 */
export function storedReader<T>(key: string, parse: (raw: string | null) => T): () => T {
  let raw: string | null | undefined;
  let value: T;
  return () => {
    const now = readStore(key);
    if (now !== raw) {
      raw = now;
      value = parse(now);
    }
    return value;
  };
}
